import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // DIRECT PHP ENDPOINTS (Silver Bullet 🔫)

  // Auth
  async login(email: string, password: string): Promise<any> {
    const response = await this.api.post('/auth/login.php', { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  }

  async register(data: any): Promise<any> {
    const response = await this.api.post('/auth/register.php', data);
    return response.data;
  }

  async logout(): Promise<void> {
    localStorage.removeItem('token');
  }

  // Company (Ignora el ID en URL, usa el token del usuario)
  async getCompany(): Promise<any> {
    const response = await this.api.get('/companies.php');
    return response.data;
  }

  async updateCompany(id: number, data: any): Promise<any> {
    // Apuntamos directo a companies.php. El backend sabe qué empresa es por el Token.
    // Pasamos el ID aunque el backend lo saque del token o lo verifique
    const response = await this.api.post('/companies.php', { ...data, id });
    return response.data;
  }

  // Documents
  async getDocuments(): Promise<any> {
    const response = await this.api.get('/documents.php');
    return response.data;
  }

  async generateDocument(type: string, data: any): Promise<any> {
    const response = await this.api.post('/documents.php', { type, ...data, action: 'generate' });
    return response.data;
  }

  async downloadDocument(id: number): Promise<Blob> {
    // Usamos query param ?download_id
    const response = await this.api.get(`/documents.php?download_id=${id}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getDocumentTemplates(): Promise<any> {
    // Simulado
    return {
      data: [
        { id: 1, name: 'NDA', description: 'Acuerdo de Confidencialidad', document_type: 'contract' },
        { id: 2, name: 'Contrato Laboral', description: 'Contrato estándar', document_type: 'contract' }
      ]
    };
  }

  // Diagnosis
  async getDiagnosisResults(): Promise<any> {
    const response = await this.api.get('/diagnosis.php');
    return response.data;
  }

  async getDiagnosisQuestions(): Promise<any> {
    // Mock local de preguntas para que el diagnóstico funcione inmediatamente
    return {
      questions: [
        { id: 1, category: 'Corporativo', question: '¿La empresa cuenta con Acta Constitutiva debidamente inscrita?', options: ['Sí', 'No', 'En trámite'], weight: 10 },
        { id: 2, category: 'Corporativo', question: '¿Se celebran Asambleas Anuales de Accionistas?', options: ['Sí', 'No', 'A veces'], weight: 8 },
        { id: 3, category: 'Laboral', question: '¿Todos los empleados tienen contrato laboral firmado?', options: ['Sí', 'No', 'La mayoría'], weight: 10 },
        { id: 4, category: 'Laboral', question: '¿La empresa cuenta con Reglamento Interior de Trabajo?', options: ['Sí', 'No', 'En proceso'], weight: 5 },
        { id: 5, category: 'Fiscal', question: '¿Se presentan las declaraciones fiscales a tiempo?', options: ['Sí', 'No', 'Con retrasos'], weight: 10 },
        { id: 6, category: 'Propiedad Intelectual', question: '¿La marca de la empresa está registrada?', options: ['Sí', 'No', 'En trámite'], weight: 8 },
        { id: 7, category: 'Datos Personales', question: '¿Tienen Aviso de Privacidad visible?', options: ['Sí', 'No'], weight: 10 }
      ]
    };
  }

  async submitDiagnosis(answers: any): Promise<any> {
    // Usamos el endpoint correcto
    const response = await this.api.post('/diagnosis.php', { answers });
    return response.data;
  }

  // Roadmap
  async getRoadmap(): Promise<any> {
    const response = await this.api.get('/roadmap.php');
    return response.data;
  }

  async createRoadmapItem(data: any): Promise<any> {
    const response = await this.api.post('/roadmap.php', data);
    return response.data;
  }

  async updateRoadmapItem(id: number, data: any): Promise<any> {
    const response = await this.api.put('/roadmap.php', { ...data, id });
    return response.data;
  }

  async deleteRoadmapItem(id: number): Promise<any> {
    const response = await this.api.delete(`/roadmap.php?id=${id}`);
    return response.data;
  }

  // Risks
  async getRisks(): Promise<any> {
    const response = await this.api.get('/risks.php');
    return response.data;
  }

  async createRisk(data: any): Promise<any> {
    const response = await this.api.post('/risks.php', data);
    return response.data;
  }

  async deleteRisk(id: number): Promise<any> {
    const response = await this.api.delete(`/risks.php?id=${id}`);
    return response.data;
  }

  // Chatbot
  async sendChatMessage(message: string, conversationId?: number): Promise<any> {
    const response = await this.api.post('/chatbot.php', { message, conversationId });
    return response.data;
  }

  // Generics
  async get(endpoint: string): Promise<any> {
    const ep = endpoint.endsWith('.php') || endpoint.includes('.php') ? endpoint : endpoint + '.php';
    const response = await this.api.get(ep);
    return response.data;
  }

  async post(endpoint: string, data?: any): Promise<any> {
    const ep = endpoint.endsWith('.php') || endpoint.includes('.php') ? endpoint : endpoint + '.php';
    const response = await this.api.post(ep, data);
    return response.data;
  }

  async put(endpoint: string, data?: any): Promise<any> {
    const ep = endpoint.endsWith('.php') || endpoint.includes('.php') ? endpoint : endpoint + '.php';
    const response = await this.api.put(ep, data);
    return response.data;
  }

  async delete(endpoint: string): Promise<any> {
    const ep = endpoint.endsWith('.php') || endpoint.includes('.php') ? endpoint : endpoint + '.php';
    const response = await this.api.delete(ep);
    return response.data;
  }
}


export default new ApiService();
